import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import BaseCard, { BaseCardEvent } from 'Prototype/Level/Card/BaseCard';
import Rope from 'Prototype/Rope';
import { ICardsState } from 'State/Card/ICardState';
import CardSystem from 'System/Card/CardSystem';
import BaseCardModifierHandler from './BaseCardModifierHandler';
import { getPointsAlongRope } from 'Utility/Helpers/Rope/RopeHelper';
import TransformComponent, { TransformEvent } from 'Component/Transform';
import { Anim, StartAnimationClip, StartAnimationClipAsync } from 'System/Animation/AnimationHelper';
import { RopeGravity } from 'System/Animation/Clip/RopeGravity';
import RopeComponent from 'Component/Render/Rope';
import { RopeAppearing } from 'System/Animation/Clip/RopeAppearing';
import { RopeOnCardFlip } from 'System/Animation/Clip/RopeOnCardFlip';
import CardStateHelper from 'State/Card/CardStateHelper';
import Renderable from 'Component/Renderable';
import SpriteComponent from 'Component/Render/Sprite';
import { promisifyEvent } from 'Utility/async';
import ConnectedModifier from 'Prototype/Level/Card/Modifier/Connected';
import { TILING_ROPE_CACHE_KEY } from 'Prototype/Level/Card/Modifier/Connected';
import { getCardModifierHandler } from 'Prototype/Level/Card/ModifierTypeMap';
import GoldCard from 'Prototype/Level/Card/GoldCard';

export default class ConnectedModifierHandler extends BaseCardModifierHandler {
    public modifier: ConnectedModifier;

    constructor(modifier: ConnectedModifier) {
        super(modifier);
        this.modifier = modifier;
    }

    public init(props: { cardSystem: CardSystem }): void {
        const { cardSystem } = props;
        const cardA = cardSystem.getCardByID(this.modifierComponent.cardID);
        const cardB = this.getConnectedCard(cardSystem);

        if (!cardA || !cardB) return;

        const modifierA = this.modifier;
        const modifierB = cardB.getModifier(ConnectedModifier);

        if (!modifierA.rope && (modifierB && !modifierB.rope)) {
            const rope = this.createRope({ modifierA, cardA, modifierB, cardB, cardSystem });

            [cardA, cardB].forEach((card) => {
                card.on(GameObjectEvent.ADDED, () => {
                    if (rope.destroyed) return;
                    card.parent.addChild(rope);
                });

                card.on(GameObjectEvent.DISPLAY_PARENT_CHANGE, (displayParent) => {
                    if (rope.destroyed) return;
                    rope.setDisplayParent(displayParent);
                });
            });
        }
    }

    public handleStateChange(state: ICardsState, cardSystem: CardSystem): void {
        const cardA = cardSystem.getCardByID(this.modifierComponent.cardID);
        const cardB = this.getConnectedCard(cardSystem);
        const behaviorA = CardStateHelper.getCardBehavior(state, cardA.id);
        const behaviorB = CardStateHelper.getCardBehavior(state, cardB.id);

        this.updateViews(
            cardA,
            !behaviorA.isFaceUp(state) || behaviorA.isObscured(state),
            !behaviorB.isFaceUp(state) || behaviorB.isObscured(state),
        );
    }

    private updateViews(card: BaseCard, isBlockedA: boolean, isBlockedB: boolean): void {
        const blocked = isBlockedA || isBlockedB;
        const fadeInIcon = blocked ? this.modifier.blockedIcon : this.modifier.icon;
        const fadeOutIcon = !blocked ? this.modifier.blockedIcon : this.modifier.icon;
        const cardAlpha = (isBlockedB && !isBlockedA) || (blocked && card instanceof GoldCard) ? 0.5 : 1;

        if (fadeInIcon.getComponent(SpriteComponent).alpha !== 1) {
            Anim(fadeInIcon, { alpha: 1, duration: 300 });
        }

        if (fadeOutIcon.getComponent(SpriteComponent).alpha !== 0) {
            Anim(fadeOutIcon, { alpha: 0, duration: 300 });
        }

        if (card.getComponent(Renderable).alpha !== cardAlpha) {
            Anim(card, { alpha: cardAlpha, duration: 300 }, true);
        }
    }

    private createRope(meta: {
        modifierA: ConnectedModifier,
        cardA: BaseCard,
        modifierB: ConnectedModifier
        cardB: BaseCard,
        cardSystem: CardSystem,
    }): Rope {
        const { cardA: a, cardB: b, modifierA: ma, modifierB: mb, cardSystem } = meta;

        const points = getPointsAlongRope({
            startPoint: a.getComponent(TransformComponent).toGlobal(ConnectedModifier.ICON_OFFSET),
            endPoint: b.getComponent(TransformComponent).toGlobal(ConnectedModifier.ICON_OFFSET),
            resolution: 15,
        });

        const rope = GameObject.Instantiate(Rope, {
            texture: TILING_ROPE_CACHE_KEY,
            points,
            iconA: ma.iconContainer,
            iconB: mb.iconContainer,
            cardA: a,
            cardB: b,
            downSample: 1.5,
        });

        [ma, mb].forEach((modifier) => {
            modifier.rope = rope;

            getCardModifierHandler(modifier)
                .handleStateChange(meta.cardSystem.store.getState().levelState.cardsState, meta.cardSystem);
        })

        const cards = cardSystem.cardsContainer.children;
        cardSystem.cardsContainer.addChildAt(rope, Math.max(cards.indexOf(a), cards.indexOf(b)) + 1);

        this.startAnimations({ rope, cardA: a, cardB: b });

        return rope;
    }

    private async startAnimations(props: {
        rope: Rope;
        cardA: BaseCard;
        cardB: BaseCard;
    }): Promise<void> {
        const { rope, cardA, cardB } = props;
        const ropeComponent = rope.getComponent(RopeComponent);
        rope.getComponent(Renderable).alpha = 0;

        await Promise.all([
            promisifyEvent(cardA, BaseCardEvent.INTRO_FALL_IN_COMPLETE),
            promisifyEvent(cardB, BaseCardEvent.INTRO_FALL_IN_COMPLETE),
        ]);

        ropeComponent.resetNodes();
        StartAnimationClip(new RopeGravity(rope, {}, true));
        await StartAnimationClipAsync(new RopeAppearing(rope, {}, true));

        ropeComponent.startFollowCards();

        const onCardFlip = () => {
            StartAnimationClip(new RopeOnCardFlip(rope, {}, true));
        };

        [cardA, cardB].forEach((card) => {
            card.on(BaseCardEvent.FLIP_START, onCardFlip);
        });
    };

    private getConnectedCard(system: CardSystem): BaseCard {
        return system.getCardByID(this.modifierComponent.properties.cardID);
    }

    public setModifierFaceUp(faceUp: boolean) {
        this.updateVisibility(true);
    }

    public clearModifier(): void {
        // noop
    }
}
